import React, { useState } from "react";
import Modal from "react-modal";
import { Button } from "../../Button";
import { PartnerAccountViewModel } from "../../../api/generated/models/PartnerAccountViewModel";
import { PartnerAccountsService } from "../../../api/generated/services/PartnerAccountsService";
import { ReactComponent as CloseIcon } from "../../../assets/icons/x.svg";

interface ResendPartnerInvitationModalProps {
  isOpen: boolean;
  partnerAccount: PartnerAccountViewModel | null;
  onClose: () => void;
  onSuccess: () => void;
}

const ResendPartnerInvitationModal: React.FC<ResendPartnerInvitationModalProps> = ({
  isOpen,
  partnerAccount,
  onClose,
  onSuccess,
}) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleClose = () => {
    if (!loading) {
      setError(null);
      onClose();
    }
  };

  const handleConfirm = async () => {
    if (!partnerAccount?.id) {
      setError("Partner account ID is missing");
      return;
    }

    setLoading(true);
    setError(null);

    try {
      await PartnerAccountsService.postApiPartnerAccountsResendPartnerAccountInvitationEmail(
        {
          partnerAccountId: partnerAccount.id,
        }
      );
      handleClose();
      onSuccess();
    } catch (err: any) {
      console.error("Error resending invitation:", err);
      setError(
        err?.body?.message ||
          err?.message ||
          "Failed to resend invitation. Please try again."
      );
    } finally {
      setLoading(false);
    }
  };

  if (!partnerAccount) {
    return null;
  }

  return (
    <Modal
      className="modal-content md:max-w-[500px]"
      overlayClassName="modal-overlay"
      isOpen={isOpen}
      onRequestClose={handleClose}
      contentLabel="Resend Invitation"
    >
      <div className="flex justify-between mb-6">
        <h2 className="text-primary font-medium text-xl">Resend Invitation</h2>
        <Button onClick={handleClose} variant="icon" disabled={loading}>
          <CloseIcon className="w-3 h-3" />
        </Button>
      </div>

      <div className="space-y-4">
        {partnerAccount.acceptedInvitation ? (
          <div className="bg-blue-50 border border-blue-200 p-4 rounded-lg">
            <p className="text-sm text-blue-800">
              This user has already activated their account by setting up a
              password.
            </p>
            <p className="text-sm text-blue-800 mt-2">
              They will receive an email with a link to the login page and
              password reset instructions at{" "}
              <span className="font-medium">{partnerAccount.email}</span>.
            </p>
          </div>
        ) : (
          <div className="bg-orange-50 border border-orange-200 p-4 rounded-lg">
            <p className="text-sm text-orange-800">
              This user has not yet activated their account.
            </p>
            <p className="text-sm text-orange-800 mt-2">
              They will receive a new invitation email with a link to set their
              password at{" "}
              <span className="font-medium">{partnerAccount.email}</span>.
            </p>
            <p className="text-sm text-orange-800 mt-2 font-medium">
              Note: Only the link in the new invitation email will be valid. All
              previously sent invitation links will no longer work.
            </p>
          </div>
        )}

        {error && (
          <div className="text-red-500 text-sm bg-red-50 border border-red-200 p-3 rounded-lg">
            {error}
          </div>
        )}

        <div className="flex gap-3 justify-end pt-4">
          <Button
            type="button"
            variant="outline"
            onClick={handleClose}
            disabled={loading}
          >
            Cancel
          </Button>
          <Button onClick={handleConfirm} isLoading={loading}>
            Confirm & Resend
          </Button>
        </div>
      </div>
    </Modal>
  );
};

export default ResendPartnerInvitationModal;
